/* axc-volume-pricing.js  (Empire / L3 Tile Sandbox v2)
   One instance per .axc-vp-root. N-tier generic — reads the tier ladder
   from data attributes, so 2 tiers or 8 tiers work with no code change.

   Data source:
     data-axc-vp-base   — base price in cents (qty 1 .. first break)
     data-axc-vp-tiers  — JSON array of discount tiers [{qty,price(cents)}]
   Together they form the full ladder. This is the SAME data the MNML
   app's checkout Function uses, so display and cart never disagree.

   Hooks the Empire product form quantity controls (#product-quantity-input
   + #product-quantity-select) and the Add-to-Cart label.
*/
(function () {
  'use strict';

  function moneyFmt(dollars, symbol) {
    return (symbol || '$') + (Math.round(dollars * 100) / 100).toFixed(2);
  }

  function AxcVolumePricing(root) {
    this.root = root;
    this.currencySymbol = root.dataset.currencySymbol || '$';

    // Build the full ladder (prices in cents): base tier + discount tiers.
    var base = parseInt(root.dataset.axcVpBase, 10) || 0;
    var tiers = [];
    try { tiers = JSON.parse(root.dataset.axcVpTiers || '[]'); } catch (e) { tiers = []; }
    this.ladder = [{ qty: 1, price: base }];
    for (var i = 0; i < tiers.length; i++) {
      var q = parseInt(tiers[i].qty, 10);
      var p = parseInt(tiers[i].price, 10);
      if (!isNaN(q) && !isNaN(p)) this.ladder.push({ qty: q, price: p });
    }
    this.ladder.sort(function (a, b) { return a.qty - b.qty; });

    this.chips = root.querySelectorAll('.axc-vp-chip');
    this.qualifyingLabel = root.querySelector('[data-axc-vp-qualifying]');
    this.track = root.querySelector('.axc-vp-track');
    this.prevArrow = root.querySelector('[data-axc-vp-prev]');
    this.nextArrow = root.querySelector('[data-axc-vp-next]');

    var form = root.closest('form[action*="/cart/add"]')
            || document.querySelector('form[action*="/cart/add"]');
    this.qtyInput = form ? form.querySelector('#product-quantity-input, [data-quantity-input]') : null;
    this.qtySelect = form ? form.querySelector('#product-quantity-select, [data-quantity-select]') : null;
    this.cartBtn = form ? form.querySelector('[data-product-atc], .product-form--atc-button') : null;
    this.cartLabel = this.cartBtn ? this.cartBtn.querySelector('.atc-button--text') : null;

    this._bind();
    this.update(this._readQty());
    this._updateArrows();
    // Re-check once fonts/layout settle (chip widths can shift).
    var self0 = this;
    setTimeout(function () { self0._updateArrows(); }, 250);
  }

  // Show an arrow only when the track overflows and there is content
  // to scroll toward in that direction.
  AxcVolumePricing.prototype._updateArrows = function () {
    if (!this.track) return;
    var t = this.track;
    var maxScroll = t.scrollWidth - t.clientWidth;
    var overflowing = maxScroll > 2;
    if (this.prevArrow) {
      this.prevArrow.classList.toggle('is-hidden',
        !overflowing || t.scrollLeft <= 1);
    }
    if (this.nextArrow) {
      this.nextArrow.classList.toggle('is-hidden',
        !overflowing || t.scrollLeft >= maxScroll - 1);
    }
  };

  AxcVolumePricing.prototype._readQty = function () {
    var v = NaN;
    if (this.qtyInput && this.qtyInput.value !== '') {
      v = parseInt(this.qtyInput.value, 10);
    } else if (this.qtySelect && this.qtySelect.value && this.qtySelect.value !== '10+') {
      v = parseInt(this.qtySelect.value, 10);
    }
    if (isNaN(v) || v < 1) v = 1;
    return v;
  };

  // Index into this.ladder for a given quantity (highest tier whose qty <= q).
  AxcVolumePricing.prototype.activeIndex = function (qty) {
    var idx = 0;
    for (var i = 0; i < this.ladder.length; i++) {
      if (qty >= this.ladder[i].qty) idx = i;
    }
    return idx;
  };

  AxcVolumePricing.prototype.update = function (qty) {
    var idx = this.activeIndex(qty);
    var priceCents = this.ladder[idx].price;
    var totalDollars = (qty * priceCents) / 100;

    this.chips.forEach(function (chip) {
      var ci = parseInt(chip.dataset.axcVpIndex, 10);
      chip.classList.toggle('is-active', ci === idx);
    });

    if (this.qualifyingLabel) {
      var next = this.ladder[idx + 1];
      if (next) {
        this.qualifyingLabel.textContent =
          'Add ' + next.qty + '+ to drop to '
          + moneyFmt(next.price / 100, this.currencySymbol) + '/unit';
      } else {
        this.qualifyingLabel.textContent =
          'Best price unlocked — '
          + moneyFmt(priceCents / 100, this.currencySymbol) + '/unit';
      }
    }

    if (this.cartLabel) {
      this.cartLabel.textContent =
        'Add ' + qty + ' to Cart — ' + moneyFmt(totalDollars, this.currencySymbol);
    }
  };

  AxcVolumePricing.prototype._setQty = function (newQty) {
    // Select first (so Empire's handlers run), then the input last so it
    // keeps the real value.
    if (this.qtySelect) {
      var optionValue = newQty >= 10 ? '10+' : String(newQty);
      for (var i = 0; i < this.qtySelect.options.length; i++) {
        if (this.qtySelect.options[i].value === optionValue) {
          this.qtySelect.selectedIndex = i;
          this.qtySelect.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    }
    if (this.qtyInput) {
      this.qtyInput.value = String(newQty);
      this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.update(newQty);
  };

  AxcVolumePricing.prototype._bind = function () {
    var self = this;

    if (this.qtyInput) {
      this.qtyInput.addEventListener('input', function () { self.update(self._readQty()); });
      this.qtyInput.addEventListener('change', function () { self.update(self._readQty()); });
    }
    if (this.qtySelect) {
      this.qtySelect.addEventListener('change', function () {
        if (self.qtySelect.value === '10+') {
          var cur = parseInt(self.qtyInput && self.qtyInput.value, 10);
          if (self.qtyInput && (isNaN(cur) || cur < 10)) self.qtyInput.value = '10';
        } else {
          var v = parseInt(self.qtySelect.value, 10);
          if (!isNaN(v) && self.qtyInput) self.qtyInput.value = String(v);
        }
        self.update(self._readQty());
      });
    }

    // Clicking a chip jumps the quantity to that tier's threshold.
    this.chips.forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        var jumpQty = parseInt(chip.dataset.axcVpQty, 10);
        if (!isNaN(jumpQty) && jumpQty > 0) self._setQty(jumpQty);
      });
    });

    // Carousel arrows — scroll the track, refresh arrow visibility.
    if (this.track) {
      this.track.addEventListener('scroll', function () { self._updateArrows(); });
    }
    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', function (e) {
        e.preventDefault();
        self.track.scrollBy({ left: -self.track.clientWidth * 0.8, behavior: 'smooth' });
      });
    }
    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', function (e) {
        e.preventDefault();
        self.track.scrollBy({ left: self.track.clientWidth * 0.8, behavior: 'smooth' });
      });
    }
    window.addEventListener('resize', function () { self._updateArrows(); });
  };

  function boot() {
    document.querySelectorAll('.axc-vp-root').forEach(function (el) {
      if (el._axcVpBound) return;
      el._axcVpBound = true;
      new AxcVolumePricing(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
